import { useState, useEffect, useRef } from "react";
import { useAuth } from "../hooks/useAuth";
import { useInstallPrompt } from "../hooks/useInstallPrompt";
import { getPlayerStats } from "../lib/scores";
import type { PlayerStats } from "../lib/storage";
import { useLocale } from "../lib/i18n";
import { BRAND } from "../lib/brand";
import { supabase } from "../lib/supabase";
import { T, Mark, type MarkKind } from "../components/v2";

interface ProfileProps {
  onNavigate?: (page: string) => void;
}

export function Profile({ onNavigate }: ProfileProps) {
  const auth = useAuth();
  const { t, locale } = useLocale();
  const he = locale === "he";
  const [stats, setStats] = useState<PlayerStats | null>(null);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const editRef = useRef<HTMLInputElement>(null);
  const { isIOS, install, deferredPrompt } = useInstallPrompt();
  const isInstalled = window.matchMedia("(display-mode: standalone)").matches;
  const canInstall = !isInstalled && (!!deferredPrompt || isIOS);

  useEffect(() => {
    getPlayerStats().then(setStats);
  }, []);

  // ── Not signed in ──
  if (!auth.user) {
    return (
      <div
        dir={he ? "rtl" : "ltr"}
        className="px-5 pt-4 pb-2 flex flex-col items-center"
        style={{ color: T.cream, fontFamily: T.sans, gap: 16 }}
      >
        <div
          style={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${T.gold} 0%, ${T.goldDeep} 100%)`,
            display: "grid",
            placeItems: "center",
            boxShadow: T.shadowGold,
            border: `3px solid ${T.navyMid}`,
          }}
        >
          <Mark kind="ear" size={36} color={T.navyDeep} strokeWidth={1.6} />
        </div>
        <div style={{ textAlign: "center" }}>
          <h2
            style={{
              fontFamily: T.serif,
              fontSize: 24,
              fontWeight: 800,
              color: T.cream,
            }}
          >
            {t("profile.title")}
          </h2>
          <p style={{ fontSize: 12.5, color: T.mist, marginTop: 4, lineHeight: 1.5, maxWidth: 280 }}>
            {t("profile.signInDesc")}
          </p>
        </div>
        <button
          onClick={auth.signInWithGoogle}
          className="nig-press"
          style={{
            background: T.gold,
            color: T.navyDeep,
            border: 0,
            padding: "12px 26px",
            borderRadius: 99,
            fontFamily: T.sans,
            fontSize: 14,
            fontWeight: 700,
            boxShadow: T.shadowGold,
          }}
        >
          {t("profile.signInGoogle")}
        </button>
        {onNavigate && (
          <button
            onClick={() => onNavigate("home")}
            className="nig-press"
            style={{
              background: "transparent",
              color: T.mist,
              border: 0,
              fontSize: 12.5,
              textDecoration: "underline",
            }}
          >
            {t("profile.continueAsGuest")}
          </button>
        )}
      </div>
    );
  }

  // ── Signed in ──
  const totalScore = stats?.totalScore ?? 0;
  const totalPlayed = stats?.totalPlayed ?? 0;
  const currentStreak = stats?.currentStreak ?? 0;
  const maxStreak = stats?.maxStreak ?? 0;
  // Best stage = lowest "1..6" key with non-zero count; 0 = miss
  const bestStage = (() => {
    const dist = stats?.stageDistribution ?? {};
    for (const k of ["1", "2", "3", "4", "5", "6"]) if ((dist[k] ?? 0) > 0) return parseInt(k, 10);
    return 0;
  })();
  const accuracyPct = totalPlayed > 0
    ? Math.round(((totalPlayed - (stats?.stageDistribution?.["0"] ?? 0)) / totalPlayed) * 100)
    : 0;

  const achievements: Array<{ mark: MarkKind; label: string; earned: boolean; sub?: string }> = [
    { mark: "streak", label: he ? "רצף 7 ימים" : "7-day streak", earned: maxStreak >= 7 },
    { mark: "medal",  label: he ? "מאה זיהויים" : "100 plays", earned: totalPlayed >= 100 },
    { mark: "bolt",   label: he ? "תו אחד" : "single-note", earned: bestStage === 1 && bestStage > 0, sub: he ? "פיצוח בתו 1" : "guess on note 1" },
    { mark: "spark",  label: he ? "אלף נקודות" : "1k points", earned: totalScore >= 1000 },
    { mark: "crown",  label: he ? "אלף ניגונים" : "1k niggunim", earned: totalPlayed >= 1000, sub: he ? "1000 זיהויים" : undefined },
    { mark: "moon",   label: he ? "חצות" : "Midnight", earned: false, sub: he ? "אחרי 24:00" : "after midnight" },
  ];

  return (
    <div
      dir={he ? "rtl" : "ltr"}
      className="px-5 pt-1 pb-2 nig-stagger"
      style={{ color: T.cream, fontFamily: T.sans }}
    >
      {/* Avatar + name */}
      <div style={{ textAlign: "center", marginBottom: 14 }}>
        <div
          style={{
            width: 76,
            height: 76,
            borderRadius: "50%",
            margin: "0 auto 10px",
            background: `linear-gradient(135deg, ${T.gold} 0%, ${T.goldDeep} 100%)`,
            display: "grid",
            placeItems: "center",
            fontFamily: T.serif,
            fontSize: 32,
            fontWeight: 900,
            color: T.navyDeep,
            boxShadow: T.shadowGold,
            border: `3px solid ${T.navyMid}`,
          }}
        >
          {(auth.displayName ?? "?")[0].toUpperCase()}
        </div>
        {editing ? (
          <div style={{ display: "flex", justifyContent: "center", gap: 6, alignItems: "center" }}>
            <input
              ref={editRef}
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              maxLength={30}
              onKeyDown={(e) => {
                if (e.key === "Enter") { auth.updateDisplayName(editName); setEditing(false); }
                else if (e.key === "Escape") setEditing(false);
              }}
              style={{
                background: T.creamWarm,
                color: T.ink,
                border: `1px solid ${T.gold}`,
                borderRadius: 10,
                padding: "6px 10px",
                fontSize: 14,
                fontFamily: T.sans,
                outline: "none",
                width: 180,
                textAlign: "center",
              }}
            />
            <button
              onClick={() => { auth.updateDisplayName(editName); setEditing(false); }}
              style={{ background: "transparent", border: 0, color: T.gold, fontSize: 14, fontWeight: 700, cursor: "pointer" }}
            >
              ✓
            </button>
            <button
              onClick={() => setEditing(false)}
              style={{ background: "transparent", border: 0, color: T.mist, fontSize: 14, cursor: "pointer" }}
            >
              ×
            </button>
          </div>
        ) : (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontFamily: T.serif, fontSize: 19, fontWeight: 700, color: T.cream }}>
              {auth.displayName}
            </div>
            <button
              onClick={() => { setEditName(auth.displayName ?? ""); setEditing(true); setTimeout(() => editRef.current?.focus(), 0); }}
              aria-label="edit name"
              style={{
                background: "transparent",
                border: 0,
                color: T.mist,
                fontSize: 12,
                cursor: "pointer",
                padding: 4,
              }}
            >
              ✎
            </button>
          </div>
        )}
        <div style={{ fontSize: 11, color: T.mist, marginTop: 2 }}>
          {t("profile.joined", {
            date: new Date(auth.user.created_at).toLocaleDateString(he ? "he-IL" : "en-US", {
              month: "short",
              year: "numeric",
            }),
          })}
        </div>
      </div>

      {/* 2x2 big stats */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 8,
          marginBottom: 14,
        }}
      >
        <BigStat value={totalScore.toLocaleString()} label={he ? "ניקוד כולל" : "lifetime score"} accent />
        <BigStat value={String(currentStreak)} label={he ? "רצף נוכחי" : "current streak"} mark="streak" />
        <BigStat value={String(totalPlayed)} label={he ? "ניגונים זוהו" : "niggunim played"} />
        <BigStat value={`${accuracyPct}%`} label={he ? "דיוק" : "accuracy"} />
      </div>

      {/* Achievements */}
      <div style={{ marginBottom: 12 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            marginBottom: 8,
          }}
        >
          <span style={{ fontFamily: T.serif, fontSize: 16, fontWeight: 700 }}>
            {he ? "הישגים" : "Achievements"}
          </span>
          <span style={{ fontSize: 11, color: T.gold }}>
            {achievements.filter((a) => a.earned).length} / {achievements.length}
          </span>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 8,
          }}
        >
          {achievements.map((a, i) => (
            <div
              key={i}
              style={{
                aspectRatio: "1",
                borderRadius: T.r2,
                padding: 8,
                background: a.earned
                  ? `linear-gradient(155deg, ${T.gold}20, ${T.navyMid})`
                  : T.navyMid,
                border: `1px solid ${a.earned ? T.gold + "50" : T.hairline}`,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                opacity: a.earned ? 1 : 0.6,
                minHeight: 0,
              }}
            >
              <Mark
                kind={a.mark}
                size={22}
                color={a.earned ? T.gold : T.mist}
                strokeWidth={1.4}
              />
              <div>
                <div
                  style={{
                    fontSize: 10.5,
                    fontWeight: 600,
                    color: a.earned ? T.cream : T.mist,
                    lineHeight: 1.2,
                  }}
                >
                  {a.label}
                </div>
                {a.sub && (
                  <div style={{ fontSize: 9, color: T.mistFaint, marginTop: 1, lineHeight: 1.2 }}>
                    {a.sub}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Actions row — small icon-only chips */}
      <div
        style={{
          display: "flex",
          gap: 6,
          flexWrap: "wrap",
          justifyContent: "center",
          marginTop: "auto",
        }}
      >
        {canInstall && (
          <ActionPill
            label={t("profile.addToHome")}
            onClick={() => {
              if (isIOS) {
                const isSafari =
                  /Safari/.test(navigator.userAgent) &&
                  !/CriOS|FxiOS|EdgiOS/.test(navigator.userAgent);
                alert(isSafari ? t("profile.installIOS.safari") : t("profile.installIOS.other"));
              } else {
                install();
              }
            }}
          />
        )}
        <ActionPill
          label={t("profile.shareProfile")}
          onClick={async () => {
            const text = t("profile.shareText");
            const url = BRAND.siteUrl;
            if (navigator.share) {
              try { await navigator.share({ text, url }); } catch { /* user cancelled */ }
            } else {
              await navigator.clipboard.writeText(`${text} ${url}`);
              alert(t("profile.linkCopied"));
            }
          }}
        />
        <ActionPill
          label={t("profile.contactUs")}
          onClick={() => { window.location.href = `mailto:${BRAND.contactEmail}`; }}
        />
        <ActionPill label={t("common.signOut")} onClick={auth.signOut} />
        <ActionPill
          label={t("profile.deleteAccount")}
          danger
          onClick={async () => {
            if (!confirm(t("profile.deleteAccountConfirm"))) return;
            if (!confirm(t("profile.deleteAccountConfirm2"))) return;
            try {
              const { data: { session } } = await supabase.auth.getSession();
              if (!session) { alert(t("profile.deleteAccountFailed")); return; }
              const resp = await fetch(
                `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-account`,
                {
                  method: "POST",
                  headers: { Authorization: `Bearer ${session.access_token}` },
                },
              );
              if (!resp.ok) {
                console.error("delete-account failed", resp.status, await resp.text());
                alert(t("profile.deleteAccountFailed"));
                return;
              }
              await auth.signOut();
              alert(t("profile.deleteAccountDone"));
            } catch (err) {
              console.error(err);
              alert(t("profile.deleteAccountFailed"));
            }
          }}
        />
      </div>
    </div>
  );
}

function BigStat({
  value,
  label,
  accent,
  mark,
}: {
  value: string;
  label: string;
  accent?: boolean;
  mark?: MarkKind;
}) {
  return (
    <div
      style={{
        padding: "10px 14px",
        borderRadius: T.r2,
        background: T.navyMid,
        border: `1px solid ${T.hairline}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div
          style={{
            fontFamily: T.serif,
            fontSize: 22,
            fontWeight: 800,
            color: accent ? T.gold : T.cream,
            lineHeight: 1,
          }}
        >
          {value}
        </div>
        {mark && <Mark kind={mark} size={18} color={T.gold} strokeWidth={1.4} />}
      </div>
      <div style={{ fontSize: 10.5, color: T.mist, marginTop: 4, letterSpacing: "0.04em" }}>
        {label}
      </div>
    </div>
  );
}

function ActionPill({
  label,
  onClick,
  danger,
}: {
  label: string;
  onClick?: () => void;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className="nig-press nig-tap"
      style={{
        padding: "7px 12px",
        borderRadius: 99,
        background: "transparent",
        border: `1px solid ${danger ? "#ff8a8a40" : T.hairline}`,
        color: danger ? "#ff8a8a" : T.mist,
        fontFamily: T.sans,
        fontSize: 11.5,
        fontWeight: 500,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}
